"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Star } from "lucide-react";
import { ProfileReviewsDialog } from "@/components/reviews/profile-reviews-dialog";
import {
  ProfileStatInline,
  ProfileStatSeparator,
  ProfileRatingStatSkeleton,
} from "@/components/Profile/profile-stat-inline";
import {
  usePartyReviewStats,
  type PartyReviewStatsSnapshot,
} from "@/hooks/use-party-review-stats";
import { cn } from "@/lib/utils";

const PROFILE_STAT_TEXT = "text-[11px] font-medium tracking-[0.08px]";

export const profileStatLineClassName = `${PROFILE_STAT_TEXT} text-foreground inline-flex min-w-0 flex-wrap items-center gap-x-0 gap-y-0.5`;

export { ProfileStatSeparator };

export function ProfileStatSegment({
  stat,
  value,
  loading = false,
}: {
  stat: "listings" | "sales" | "followers";
  value: number;
  loading?: boolean;
}) {
  return <ProfileStatInline stat={stat} value={value} loading={loading} />;
}

type ProfileReviewScoreLinkProps = {
  profileId: string;
  enabled?: boolean;
  /**
   * Already-resolved stats for `profileId`. When supplied this component does
   * NOT fetch — it renders what the parent already has.
   *
   * Exists because `OrderPartyCard` fetches these stats itself and only renders
   * this component once they say the profile HAS reviews. Letting the hook run
   * again meant a second, strictly-later request for the same profile: measured
   * on prod 2026-07-29 as two serial waves (card at +1691ms → link at +2182ms),
   * ×2 responsive subtrees = 4 identical requests per listing pageview.
   */
  stats?: PartyReviewStatsSnapshot;
  className?: string;
  tone?: "default" | "dark" | "profile";
  showSkeletonWhileLoading?: boolean;
  // When viewing your own profile, surface the "Given" tab (reviews you wrote).
  isOwnProfile?: boolean;
  renderTrigger?: (props: {
    label: string;
    onClick: () => void;
    hasNumericRating: boolean;
  }) => ReactNode;
  renderSkeleton?: () => ReactNode;
};

export function ProfileReviewScoreLink({
  profileId,
  enabled = true,
  stats,
  className,
  tone = "default",
  showSkeletonWhileLoading = false,
  isOwnProfile = false,
  renderTrigger,
  renderSkeleton,
}: ProfileReviewScoreLinkProps) {
  const tParty = useTranslations("listing.partyCard");
  // Parent-supplied stats short-circuit the request; the hook still runs (rules
  // of hooks) but `enabled=false` makes it a no-op.
  const fetched = usePartyReviewStats(profileId, enabled && stats === undefined);
  const { rating, reviewCount, profileReviewRole } = stats ?? fetched;
  const isLoading = stats === undefined && fetched.isLoading;
  const [open, setOpen] = useState(false);

  const hasNumericRating =
    typeof rating === "number" && Number.isFinite(rating);
  const hasReviewCount =
    typeof reviewCount === "number" && Number.isFinite(reviewCount);
  const hasPublishedReviews = hasReviewCount && reviewCount > 0;

  if (!enabled) {
    return null;
  }

  if (isLoading) {
    if (!showSkeletonWhileLoading) return null;
    return renderSkeleton ? (
      renderSkeleton()
    ) : (
      <ProfileRatingStatSkeleton className={className} />
    );
  }

  if (!hasPublishedReviews) {
    return null;
  }

  const label = hasNumericRating
    ? rating.toFixed(1)
    : tParty("ratingNew");

  const defaultTrigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={cn(
        "inline min-w-0 max-w-full shrink align-baseline underline underline-offset-2 transition-opacity hover:opacity-80",
        PROFILE_STAT_TEXT,
        tone === "dark" ? "text-white" : "text-foreground",
        className,
      )}
    >
      <Star
        className={cn(
          "mr-0.5 inline-block h-[11px] w-[11px] align-[-1px]",
          hasNumericRating && "fill-amber-500 text-amber-500",
        )}
      />
      <span className="font-bold tabular-nums">{label}</span>
    </button>
  );

  return (
    <>
      {renderTrigger
        ? renderTrigger({
            label,
            onClick: () => setOpen(true),
            hasNumericRating,
          })
        : defaultTrigger}
      <ProfileReviewsDialog
        open={open}
        onOpenChange={setOpen}
        profileId={profileId}
        initialRole={profileReviewRole ?? "seller"}
        showGiven={isOwnProfile}
      />
    </>
  );
}
